"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ArrowDownUp, Copy, FileUp } from "lucide-react"
import { toast } from "sonner"
import Image from "next/image"

export function Base64Converter() {
  const [inputText, setInputText] = useState("")
  const [outputText, setOutputText] = useState("")
  const [mode, setMode] = useState<"encode" | "decode">("encode")
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      if (file.type.startsWith("image/")) {
        setImagePreview(result)
      }
      // Get the base64 part after the comma
      const base64String = result.split(",")[1]
      setInputText(base64String)
    }
    reader.readAsDataURL(file)
  }

  const handleConvert = () => {
    try {
      if (mode === "encode") {
        const encoded = btoa(inputText)
        setOutputText(encoded)
        setImagePreview(null)
      } else {
        const decoded = atob(inputText)
        setOutputText(decoded)

        // Try to detect if it's an image and show preview
        try {
          if (/^<svg/.test(decoded) || /^\s*<\?xml/.test(decoded)) {
            // It's SVG
            const blob = new Blob([decoded], { type: "image/svg+xml" })
            setImagePreview(URL.createObjectURL(blob))
          } else {
            setImagePreview(null)
          }
        } catch {
          setImagePreview(null)
        }
      }

      toast.success(`Successfully ${mode === "encode" ? "encoded to" : "decoded from"} Base64`)
    } catch (error) {
      toast.error(`Conversion failed: ${error instanceof Error ? error.message : "Invalid input"}`)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText)
    toast.success("Copied to clipboard!")
  }

  const toggleMode = () => {
    setMode(mode === "encode" ? "decode" : "encode")
    setInputText(outputText)
    setOutputText("")
    setImagePreview(null)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Base64 Converter</CardTitle>
        <CardDescription>
          Convert text to and from Base64 encoding. Upload files to encode or paste Base64 to decode.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={mode} onValueChange={(value) => setMode(value as "encode" | "decode")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="encode">Encode to Base64</TabsTrigger>
            <TabsTrigger value="decode">Decode from Base64</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Input</label>
            <Textarea
              placeholder={mode === "encode" ? "Enter text to encode" : "Enter Base64 to decode"}
              className="min-h-[200px] font-mono text-sm"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <div className="flex justify-between">
              <Button variant="outline" size="sm" onClick={() => setInputText("")}>
                Clear
              </Button>
              {mode === "encode" && (
                <div className="relative">
                  <input
                    type="file"
                    id="fileUpload"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileUpload}
                  />
                  <Button variant="outline" size="sm">
                    <FileUp className="h-4 w-4 mr-2" />
                    Upload File
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Output</label>
            <Textarea
              placeholder="Result will appear here"
              className="min-h-[200px] font-mono text-sm"
              value={outputText}
              readOnly
            />
            <div className="flex justify-between">
              {imagePreview && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">Image Preview:</p>
                  <div className="border rounded p-2 bg-background">
                    <Image
                      src={imagePreview || "/placeholder.svg"}
                      alt="Preview"
                      className="max-h-[150px] max-w-full object-contain"
                    />
                  </div>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={!outputText}>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 pt-4">
          <Button onClick={handleConvert} disabled={!inputText}>
            {mode === "encode" ? "Encode" : "Decode"}
          </Button>
          <Button variant="outline" onClick={toggleMode} disabled={!outputText}>
            <ArrowDownUp className="h-4 w-4 mr-2" />
            Swap
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

